import { CadencePeriod } from 'common/types/chart';
import { Country, Wave, Brand } from 'common/types/common';
import { SegmentGroups, Segment } from 'common/types/segments';
import { KPI } from './kpi';
import { CustomKpi } from './customKpi';

export interface FilterOptions {
  country: Country[];
  wave: Wave[];
  kpi: KPI[];
  brand: Brand[];
  segment: Segment[];
  cadences: Cadences;
}

export type FetchFilters = {
  loading: boolean;
  filters: RestrictionsPayload | undefined;
  fetchFilters: (filters: Record<string, number[]>) => Promise<RestrictionsPayload | undefined>;
};

export interface CadenceEntry {
  id: number; // Replace with string during cleanup phase
  date: string;
  name: string;
  wave_ids: Array<number>;
}

export interface Cadences {
  [CadencePeriod.WEEKLY]: CadenceEntry[];
  [CadencePeriod.MONTHLY]: CadenceEntry[];
  [CadencePeriod.QUARTERLY]: CadenceEntry[];
  [CadencePeriod.ANNUALLY]: CadenceEntry[];
  [CadencePeriod.BI_ANNUALLY]: CadenceEntry[];
}

export interface Filters extends RestrictionsPayload {
  segments: Segment[];
}

export interface AllFiltersData extends Filters {
  kpis: Record<string, string>;
}

export interface ChartFilter {
  label: string;
  id: FilterNames;
  optionsOrder?: (string | number)[];
  options: FilterOption[] | undefined;
}

export enum FilterNames {
  COUNTRY = 'country',
  WAVE = 'wave',
  KPI = 'kpi',
  BRAND = 'brand',
  SEGMENT = 'segment',
}

export interface RestrictionsPayload {
  waves: Wave[];
  brands: Brand[];
  countries: Country[];
  cadences: Cadences;
}

// [0] - all waves
// [1] - most recent
// [n] - specific amount of waves
export enum DynamicFilters {
  ALL_WAVES,
  LAST_WAVE,
}

export interface Restrictions extends RestrictionsPayload {
  study_uuid: string;
}

export type ActiveFilter = 'wave' | 'brand' | 'country';

export interface RestrictionConfig {
  name: string;
  values: number[];
}

export type FilterOption = IFilterOptionGroup | IFilterOption;
export interface IFilterOption {
  value: string | number;
  label: string;
  // will be used for selected values in filter tabs, since there can be multiple options
  // with the same name, for example month names
  filterLabel?: string;
  type: FilterOptionType.OPTION;
  optionOrder?: number; // control option order
  readOnly?: boolean;
  group?: IFilterOptionGroup;
  options?: IFilterOption[]; // for group options, to keep the same structure as in API response
  color?: string; // for kpi options, to keep the same structure as in API response
  // custom KPI whose source options are gone: listed with a warning, can be deselected but not selected
  unavailable?: boolean;
}

export interface IFilterOptionGroup {
  label: string;
  options: IFilterOption[];
  type: FilterOptionType.GROUP;
  infoMessage?: string;
  group?: IFilterOptionGroup;
}

export enum FilterOptionType {
  GROUP = 'group',
  OPTION = 'option',
  SPACE = 'space',
}

export interface FiltersOptions {
  availableFilters: RestrictionsPayload | undefined;
  brands: Brand[];
  segmentGroups: SegmentGroups[];
  kpis: KPI[];
  selectedKPIFormulas: Record<string, string> | null;
  cadenceEntry?: CadenceEntry[];
  // only for chart types that offer custom KPIs (see useVisibleCustomKpis)
  customKpis?: CustomKpi[];
}

export interface IFilterColumnOption extends IFilterOption {
  type: FilterOptionType.OPTION;
  group?: IFilterOptionGroup;
}

interface IFilterColumnSpace {
  type: FilterOptionType.SPACE;
  size: number;
  label?: string;
  group?: IFilterOptionGroup;
}

export interface IFilterGroupOption {
  group?: IFilterOptionGroup;
  label: string;
  type: FilterOptionType;
  value: string;
}

export type FilterColumnOption = IFilterOptionGroup | IFilterColumnOption | IFilterColumnSpace;

export interface RawCadences {
  weeks: CadenceEntry[];
  months: CadenceEntry[];
  quarters: CadenceEntry[];
  half_years: CadenceEntry[];
  years: CadenceEntry[];
}

export interface RawRestriction {
  waves: Wave[];
  brands: Brand[];
  countries: Country[];
  cadences: RawCadences;
  study_uuid: string;
}
